import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { FaStar, FaTimes } from "react-icons/fa";
import {
  getCompareDeliveryLabel,
  getCompareExportCustom,
  getCompareMoqLabel,
  getCompareSpecItems,
  parseCompareRatingValue,
} from "../../utils/marketplaceProductCompare.util";

function CompareRating({ item }) {
  const rating = parseCompareRatingValue(item);
  if (rating == null) return <span className="marketplace-compare-modal__muted">—</span>;

  return (
    <div className="marketplace-compare-modal__rating">
      <span className="marketplace-compare-modal__stars" aria-hidden>
        {Array.from({ length: 5 }).map((_, index) => (
          <FaStar key={index} className={index + 1 <= Math.round(rating) ? "is-filled" : ""} />
        ))}
      </span>
      <span className="marketplace-compare-modal__rating-value">{rating.toFixed(1)}</span>
    </div>
  );
}

function CompareExportCustom({ item }) {
  const { exportReady, customMfg } = getCompareExportCustom(item);

  return (
    <div className="marketplace-compare-modal__flags">
      <span className={`marketplace-compare-modal__flag${exportReady ? " is-export-yes" : " is-muted"}`}>
        {exportReady ? "✓ Export Ready" : "✗ No Export"}
      </span>
      <span className={`marketplace-compare-modal__flag${customMfg ? " is-custom-yes" : " is-muted"}`}>
        {customMfg ? "✓ Custom Mfg" : "✗ No Custom"}
      </span>
    </div>
  );
}

function CompareSpecs({ item }) {
  const specs = getCompareSpecItems(item).slice(0, 4);
  if (!specs.length) return <span className="marketplace-compare-modal__muted">—</span>;

  return (
    <div className="marketplace-compare-modal__spec-list">
      {specs.map((spec) => (
        <div key={`${spec.label}-${spec.value}`} className="marketplace-compare-modal__spec-item">
          <span className="marketplace-compare-modal__spec-label">{spec.label}:</span> {spec.value}
        </div>
      ))}
    </div>
  );
}

function CompareCertifications({ item }) {
  const badges = Array.isArray(item?.cert_badges) ? item.cert_badges : [];
  if (!badges.length) return <span className="marketplace-compare-modal__muted">—</span>;

  return (
    <div className="marketplace-compare-modal__cert-list">
      {badges.map((badge) => (
        <span key={badge} className="marketplace-compare-modal__cert">
          {badge}
        </span>
      ))}
    </div>
  );
}

const COMPARE_ROWS = [
  { key: "price", label: "Price", getValue: (item) => item.price_label || "—" },
  { key: "moq", label: "MOQ", getValue: (item) => getCompareMoqLabel(item) },
  { key: "delivery", label: "Delivery", getValue: (item) => getCompareDeliveryLabel(item) },
  { key: "location", label: "Location", getValue: (item) => item.location || "—" },
  { key: "rating", label: "Rating", render: (item) => <CompareRating item={item} /> },
  { key: "specs", label: "Specs", render: (item) => <CompareSpecs item={item} /> },
  { key: "certifications", label: "Certifications", render: (item) => <CompareCertifications item={item} /> },
  { key: "export-custom", label: "Export / Custom", render: (item) => <CompareExportCustom item={item} /> },
];

export default function MarketplaceProductComparisonModal({ open, products = [], onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || products.length < 2) return null;

  return (
    <div className="marketplace-compare-modal-backdrop" onClick={onClose} role="presentation">
      <div
        className="marketplace-compare-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="marketplace-compare-modal-title"
      >
        <header className="marketplace-compare-modal__header">
          <h2 id="marketplace-compare-modal-title">Product Comparison</h2>
          <button type="button" className="marketplace-compare-modal__close" onClick={onClose} aria-label="Close">
            <FaTimes aria-hidden />
          </button>
        </header>

        <div className="marketplace-compare-modal__body">
          <table className="marketplace-compare-modal__table">
            <thead>
              <tr>
                <th className="marketplace-compare-modal__attr-head">Attribute</th>
                {products.map((item) => (
                  <th key={item.id} className="marketplace-compare-modal__product-head">
                    <div className="marketplace-compare-modal__product-title">{item.title}</div>
                    {item.company_name ? (
                      <div className="marketplace-compare-modal__product-company">{item.company_name}</div>
                    ) : null}
                    {item.verified ? (
                      <span className="marketplace-compare-modal__verified">✓ VERIFIED</span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMPARE_ROWS.map((row) => (
                <tr key={row.key} className="marketplace-compare-modal__row">
                  <td className="marketplace-compare-modal__attr-cell">{row.label}</td>
                  {products.map((item) => (
                    <td key={`${row.key}-${item.id}`} className="marketplace-compare-modal__value-cell">
                      {row.render ? row.render(item) : row.getValue(item)}
                    </td>
                  ))}
                </tr>
              ))}
              <tr className="marketplace-compare-modal__row marketplace-compare-modal__row--actions">
                <td className="marketplace-compare-modal__attr-cell" />
                {products.map((item) => {
                  const detailHref = item.details_href || `/marketplace/products/${item.id}`;
                  return (
                    <td key={`action-${item.id}`} className="marketplace-compare-modal__value-cell">
                      <Link to={detailHref} className="marketplace-compare-modal__quote-btn">
                        Request Quote
                      </Link>
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
